import { useId, useState, type FormEvent } from "react";
import { useRotateSecret } from "../../api/queries";
import type { SecretSummary } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface RotateSecretModalProps {
  /** The secret being rotated, or null when the dialog is closed — driving
   * this off the target rather than a separate boolean keeps "which secret"
   * and "is it open" from ever disagreeing. */
  secret: SecretSummary | null;
  onClose: () => void;
}

export function RotateSecretModal({ secret, onClose }: RotateSecretModalProps) {
  const valueId = useId();
  const { pushToast } = useToast();
  const mutation = useRotateSecret();
  const [value, setValue] = useState("");

  function handleClose() {
    setValue("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!secret) return;
    const { id: secretId, name: secretName } = secret;
    mutation.mutate(
      { secretId, value },
      {
        onSuccess: () => {
          pushToast({ title: `Rotated "${secretName}".`, tone: "success" });
          handleClose();
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't rotate "${secretName}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
    setValue("");
  }

  return (
    <Modal
      open={secret !== null}
      title={secret ? `Rotate "${secret.name}"` : "Rotate secret"}
      onClose={handleClose}
      initialFocusId={valueId}
    >
      <form onSubmit={handleSubmit} noValidate>
        <p className="form-field__hint">
          Replaces the value only. The name and id stay the same; the version increments so anyone using this
          secret can confirm the rotation took.
        </p>

        <label className="form-field" htmlFor={valueId}>
          <span>New value</span>
          <input
            id={valueId}
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
          />
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isPending || !value}>
            {mutation.isPending ? "Rotating…" : "Rotate"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
